// P-07 skip logic over `PublicOptions.combos` (UserFlows P-07; Backend §11.1 step 7), ported from the mockup's
// `patient/BookingFlow.tsx` `resolve`: location → visit type → provider. A list with one option is picked for the
// patient; the clinic's default visit type is preselected when the chosen location offers it; later choices that no
// longer fit are cleared.
import type { PublicOptions } from '@pa/api/public'

export interface Choice {
  locationId: string | null
  visitTypeId: string | null
  providerId: string | null
}

export const NO_CHOICE: Choice = { locationId: null, visitTypeId: null, providerId: null }

type Options = Pick<PublicOptions, 'locations' | 'visitTypes' | 'providers' | 'combos' | 'defaultVisitTypeId'> & {
  /** The visit's C-02 link preset (task 22, D-79). Absent in older fixtures. */
  preset?: PublicOptions['preset']
}
export type OptionLocation = PublicOptions['locations'][number]
export type OptionVisitType = PublicOptions['visitTypes'][number]
export type OptionProvider = PublicOptions['providers'][number]

/** Locations with at least one bookable combination, in the server's order. */
export function locationsOf(o: Options): OptionLocation[] {
  return o.locations.filter((l) => o.combos.some((c) => c.locationId === l.id))
}

/** Visit types offered at a location, in the server's order. */
export function visitTypesAt(o: Options, locationId: string): OptionVisitType[] {
  return o.visitTypes.filter((t) => o.combos.some((c) => c.locationId === locationId && c.visitTypeId === t.id))
}

/** Providers who take this visit type at this location, in the server's order. No "any provider" option. */
export function providersFor(o: Options, locationId: string, visitTypeId: string): OptionProvider[] {
  const ids = o.combos.find((c) => c.locationId === locationId && c.visitTypeId === visitTypeId)?.providerIds ?? []
  return o.providers.filter((p) => ids.includes(p.id))
}

/** Fills in whatever needs no choice and clears what no longer fits. */
export function resolve(o: Options, d: Choice): Choice {
  const locs = locationsOf(o)
  const locationId =
    locs.length === 1 ? (locs[0]?.id ?? null) : locs.some((l) => l.id === d.locationId) ? d.locationId : null
  if (!locationId) return NO_CHOICE

  const types = visitTypesAt(o, locationId)
  const has = (id: string | null) => id !== null && types.some((t) => t.id === id)
  const visitTypeId =
    types.length === 1
      ? (types[0]?.id ?? null)
      : has(d.visitTypeId)
        ? d.visitTypeId
        : has(o.defaultVisitTypeId)
          ? o.defaultVisitTypeId
          : null
  if (!visitTypeId) return { locationId, visitTypeId: null, providerId: null }

  const provs = providersFor(o, locationId, visitTypeId)
  const providerId =
    provs.length === 1 ? (provs[0]?.id ?? null) : provs.some((p) => p.id === d.providerId) ? d.providerId : null
  return { locationId, visitTypeId, providerId }
}

/**
 * The preset parts still offered (task 22, D-79; UserFlows P-07): the patient's starting draft. A part that is no
 * longer in the lists (an unlocked `stale` part) is skipped silently. A locked preset's lists are already filtered to
 * its parts by the server. No preset → nothing.
 */
export function presetChoice(o: Options): Choice {
  const p = o.preset
  if (!p) return NO_CHOICE
  const offered = (id: string | null, list: readonly { id: string }[]) =>
    id !== null && list.some((x) => x.id === id) ? id : null
  return {
    locationId: offered(p.locationId, o.locations),
    visitTypeId: offered(p.visitTypeId, o.visitTypes),
    providerId: offered(p.providerId, o.providers),
  }
}

/** A locked preset's parts (shown as fixed rows with no controls, UserFlows P-07); nothing when unlocked or none. */
export function lockedChoice(o: Options): Choice {
  return o.preset?.locked === true && !o.preset.stale ? presetChoice(o) : NO_CHOICE
}

/** The choice as the summary shows it: a locked preset part counts as chosen even before the office is picked. */
export function shownChoice(o: Options, c: Choice): Choice {
  const fixed = lockedChoice(o)
  return {
    locationId: c.locationId ?? fixed.locationId,
    visitTypeId: c.visitTypeId ?? fixed.visitTypeId,
    providerId: c.providerId ?? fixed.providerId,
  }
}

/**
 * A pick re-resolves everything after it (changing an earlier choice clears later ones that no longer fit). A part
 * with no choice yet falls back to `preferred` (the link preset): after picking an office, the preset visit type and
 * doctor are preselected there when offered; the patient can still pick something else (unlocked).
 */
export function choose(o: Options, current: Choice, patch: Partial<Choice>, preferred: Choice = NO_CHOICE): Choice {
  const d = { ...current, ...patch }
  return resolve(o, {
    locationId: d.locationId ?? preferred.locationId,
    visitTypeId: d.visitTypeId ?? preferred.visitTypeId,
    providerId: d.providerId ?? preferred.providerId,
  })
}

export const isComplete = (c: Choice) => c.locationId !== null && c.visitTypeId !== null && c.providerId !== null

/** The whole step is skipped when there is exactly one location, one visit type there and one provider. */
export function isOneOneOne(o: Options): boolean {
  const locs = locationsOf(o)
  if (locs.length !== 1 || !locs[0]) return false
  const types = visitTypesAt(o, locs[0].id)
  if (types.length !== 1 || !types[0]) return false
  return providersFor(o, locs[0].id, types[0].id).length === 1
}
